import { createFileRoute } from '@tanstack/react-router';
import { FileInput } from '@osd/ui/file-input';
import { Stack } from '@osd/ui/stack';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/file-input — live docs for the `FileInput` primitive.
 *
 * `FileInput` wraps the native `<input type="file">` with a visible
 * filename caption + Clear button. The native picker is just an
 * unstyled "Choose File" button; this wrapper matches the rest of
 * the design system.
 *
 * Renders as a labelled surface; supports multiple files
 * (`multiple`), accepted MIME types / extensions (`accept`), and a
 * programmatic Clear via the `onClear` callback.
 */
export const Route = createFileRoute('/ui/_layout/file-input')({
  component: FileInputPage,
});

function FileInputPage() {
  return (
    <Stack gap={6}>
      <Section title="FileInput" hint="Visible filename + Clear button wrapping native file picker">
        <Stack gap={3}>
          <FileInput label="Upload a single file" />

          <FileInput label="Upload one or more images" multiple accept="image/*" />

          <FileInput
            label="Upload a PDF (max 5 MB)"
            accept="application/pdf"
            hint="PDFs only; 5 MB max."
          />
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Single file, label only"
            code={`<FileInput label="Upload a single file" />
`}
          />

          <CodeBlock
            language="tsx"
            title="Multiple files, image-only, with hint"
            code={`<FileInput
  label="Upload one or more images"
  multiple
  accept="image/*"
  hint="PNG, JPG, or WebP."
/>
`}
          />

          <CodeBlock
            language="tsx"
            title="Controlled — read files on submit"
            code={`const [files, setFiles] = useState<FileList | null>(null);

<FileInput
  label="Attach files"
  onChange={(next) => setFiles(next)}
/>

<Button onClick={() => upload(files)} disabled={!files}>
  Upload
</Button>
`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
